import { useTranslation } from 'react-i18next';
import * as S from '../../Booking.style';
import { parseRentDateTime } from '^/util/rentDateTime';
import { css } from '@emotion/css';
import { useState } from 'react';
import { InsuranceInfoBox } from '^/components/InsuranceInfo/insuranceInfo';
import { GuamRawSearchCarDefinedRSType } from '^/api/Search/Search.response.types';
import { InsurancePackageNames } from '^/types/__BrandedTypes';
import { useMediaQuery } from 'react-responsive';
import { InsuranceInfoModal } from '^/pages/ReservationDetail/mobile/components/InsuranceInfoModal';

export const RentalInfoSummary = ({
  officeInfo,
  pkgSelected,
  packageInfo,
  equipsCharge,
  hideInsuranceInfoIcon = false,
}: {
  officeInfo: GuamRawSearchCarDefinedRSType['coreInfo'] | undefined;
  pkgSelected?: string;
  packageInfo?: InsurancePackageNames[];
  equipsCharge?: GuamRawSearchCarDefinedRSType['equipsCharge'] | undefined;
  hideInsuranceInfoIcon?: boolean;
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' });

  const [showInsuranceDetailPopup, setShowInsuranceDetailPopup] = useState(false);

  const { parsedPickupTime, parsedReturnTime, timeDiff } = parseRentDateTime(
    officeInfo?.pickup.time as string,
    officeInfo?.return.time as string,
    'YYYY.MM.DD (ddd) HH:mm',
  );

  return (
    <S.RentalInfoSummaryContainer>
      {isMobile && (
        <InsuranceInfoModal
          insuranceName={pkgSelected ? t(`insurance_name.${pkgSelected}`) : '기본 할인요금'}
          insuranceServices={packageInfo as InsurancePackageNames[]}
          isOpen={showInsuranceDetailPopup}
          onCloseModal={() => setShowInsuranceDetailPopup(false)}
        />
      )}
      <S.RentalInfoSummaryRow>
        <S.RentalInfoLabelContainer>대여/반납 장소</S.RentalInfoLabelContainer>
        <S.RentalInfoDeailContainer>
          {t(`location_name.${officeInfo?.pickup.code}`)} /{' '}
          {t(`location_name.${officeInfo?.return?.code}`)}
        </S.RentalInfoDeailContainer>
      </S.RentalInfoSummaryRow>
      <S.RentalInfoSummaryRow>
        <S.RentalInfoLabelContainer>대여/반납 일시</S.RentalInfoLabelContainer>
        <S.RentalInfoDeailContainer>{`${parsedPickupTime} ~ ${parsedReturnTime} (${timeDiff})`}</S.RentalInfoDeailContainer>
      </S.RentalInfoSummaryRow>
      {!!pkgSelected && !!packageInfo && (
        <S.RentalInfoSummaryRow className="relative">
          <S.RentalInfoLabelContainer>보험 내용</S.RentalInfoLabelContainer>
          <S.RentalInfoDeailContainer>
            <div
              className={css`
                display: flex;
                width: 100%;
                gap: 5px;
                align-items: center;
              `}
            >
              {t(`insurance_name.${pkgSelected}`)}
              {!hideInsuranceInfoIcon && (
                <>
                  <img
                    src="/info-circle_14.svg"
                    width={14}
                    height={14}
                    alt=""
                    onMouseEnter={() => {
                      !isMobile && setShowInsuranceDetailPopup(true);
                    }}
                    onMouseLeave={() => {
                      !isMobile && setShowInsuranceDetailPopup(false);
                    }}
                    onClick={() => {
                      isMobile && setShowInsuranceDetailPopup(true);
                    }}
                  />
                  {!isMobile && showInsuranceDetailPopup && (
                    <InsuranceInfoBox
                      insuranceName={
                        pkgSelected ? t(`insurance_name.${pkgSelected}`) : '기본 할인요금'
                      }
                      insuranceServices={packageInfo}
                      className="min-w-[715px]"
                    />
                  )}
                </>
              )}
            </div>
          </S.RentalInfoDeailContainer>
        </S.RentalInfoSummaryRow>
      )}
      {equipsCharge !== undefined && (
        <S.RentalInfoSummaryRow>
          <>
            <S.RentalInfoLabelContainer>추가 옵션</S.RentalInfoLabelContainer>
            <S.RentalInfoDeailContainer>
              {equipsCharge.filter((equip) => Number(equip.quantity) !== 0).length > 0 ? (
                <span className="flex flex-wrap">
                  {equipsCharge
                    .filter((equip) => Number(equip.quantity) !== 0)
                    .map((equip) => {
                      return `${t(`equipment_${equip.code}`)} (${equip.quantity})`;
                    })
                    .join(' / ')}
                </span>
              ) : (
                <span>{t('none')}</span>
              )}
            </S.RentalInfoDeailContainer>
          </>
        </S.RentalInfoSummaryRow>
      )}
      {officeInfo?.pickup.description && (
        <S.RentalInfoSummaryRow>
          <S.RentalInfoLabelContainer>픽업 및 방문 안내</S.RentalInfoLabelContainer>
          <S.RentalInfoDeailContainer>{officeInfo?.pickup.description}</S.RentalInfoDeailContainer>
        </S.RentalInfoSummaryRow>
      )}
    </S.RentalInfoSummaryContainer>
  );
};
